var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, makeObservable, observable, reaction, toJS } from 'mobx';
import SsoClient from '@teliaee/telia-sso-client';
import config from '../Config';
import { cancelApiCalls } from '../util/ApiUtil';
import { languageSlug } from './TranslateStore';
import { navigate } from '@teliaee/crm-components';
const AUTH_TYPE_DEALER = 'dealerService';
class SsoStore {
    constructor(rootStore) {
        Object.defineProperty(this, "ssoClient", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "hardAuthRequired", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "auth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "src", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "clientState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "loggingOut", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "initialSsoMessageReceived", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isSsoModalOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "translateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "redirectUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "loginCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "redirectIfDealer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "openSsoFrame", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isSsoModalOpen = true;
            }
        });
        Object.defineProperty(this, "ensureLogin", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (openSsoFrame, redirectUrl) => {
                if (!this.loginLevelSufficient) {
                    this.setLoginParameters(redirectUrl, undefined, openSsoFrame);
                }
                else if (redirectUrl) {
                    navigate(redirectUrl);
                }
            }
        });
        Object.defineProperty(this, "ensureHardLogin", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (openSsoFrame, redirectUrl) => {
                this.hardAuthRequired = true;
                this.ssoClient.setOptions({ authLevel: 2 });
                this.ensureLogin(openSsoFrame, redirectUrl);
            }
        });
        Object.defineProperty(this, "getLoginUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return this.isDealer ? this.ssoClient.getDealerMandatesUrl() : this.ssoClient.getLoginUrl();
            }
        });
        Object.defineProperty(this, "setIframeUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.src = this.ssoClient.getLoginUrl({ selectMandate: true });
            }
        });
        Object.defineProperty(this, "handleLogout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.loggingOut = true;
                this.auth = null;
                this.initialSsoMessageReceived = true;
                this.redirectUrl = `${config.diilWebUrl}/${this.translateStore.currentLanguage}`;
                navigate(this.redirectUrl);
                this.isSsoModalOpen = false;
            }
        });
        Object.defineProperty(this, "doLogout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.ssoClient.doLogout();
            }
        });
        Object.defineProperty(this, "setLoginParameters", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (redirectUrl, loginCallback, openSsoFrame = true) => {
                this.setIframeUrl();
                if (openSsoFrame) {
                    this.openSsoFrame();
                }
                this.redirectUrl = redirectUrl;
                this.loginCallback = loginCallback;
                this.redirectIfDealer = true;
            }
        });
        Object.defineProperty(this, "closeSsoFrame", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isSsoModalOpen = false;
                this.setIframeUrl();
                this.redirectIfDealer = false;
            }
        });
        makeObservable(this);
        this.translateStore = rootStore.translateStore;
        this.ssoClient = SsoClient.connect({
            onLogout: this.handleLogout,
            onLogin: this.handleLogin,
            env: config.environment
        }, { locale: this.translateStore.currentLanguage, app: 'diil', autoSelectSelf: true, hideB2B: true });
        this.ssoClient.loadInitialState();
        reaction(() => this.translateStore.currentLanguage, (locale) => this.ssoClient.setOptions({ locale }));
    }
    get loggedIn() {
        var _a, _b;
        return !!((_a = this.auth) === null || _a === void 0 ? void 0 : _a.person) || !!((_b = this.auth) === null || _b === void 0 ? void 0 : _b.dealer);
    }
    get loggedInWithHardAuth() {
        return this.loggedIn && this.auth.authLevel > 1;
    }
    get loggedInWithWeakAuth() {
        return this.loggedIn && this.auth.authLevel === 1;
    }
    get loginLevelSufficient() {
        return this.hardAuthRequired ? this.loggedInWithHardAuth : this.loggedIn;
    }
    get person() {
        return this.auth && this.auth.person;
    }
    get customer() {
        return this.auth && this.auth.customer;
    }
    get isPrivateCustomer() {
        return this.auth && this.auth.customer && this.auth.customer.type === 'B2C';
    }
    get ssoClientState() {
        return toJS(this.clientState);
    }
    get isRepresentingSelf() {
        return (this.person &&
            this.customer &&
            this.person.personalCode === this.customer.registrationNumber &&
            this.person.personalCodeCountryCode === this.customer.registrationNumberCountryCode);
    }
    get dealer() {
        return this.auth && this.auth.dealer;
    }
    get ssoFrameVisible() {
        return this.ssoClient && this.isSsoModalOpen;
    }
    get ssoLoaded() {
        return this.initialSsoMessageReceived;
    }
    get customerId() {
        return this.customer && this.customer.customerId;
    }
    get isDealer() {
        return this.auth && this.auth.authType === AUTH_TYPE_DEALER;
    }
    allowWeakAuth() {
        this.hardAuthRequired = false;
        this.ssoClient.setOptions({ authLevel: 1 });
    }
    handleLogin(ssoResult) {
        var _a, _b;
        if (this.hasCustomerIdChanged(ssoResult)) {
            cancelApiCalls();
        }
        this.initialSsoMessageReceived = true;
        this.clientState = ssoResult;
        const isSameCustomer = ((_a = this.auth) === null || _a === void 0 ? void 0 : _a.customer) &&
            ((_b = ssoResult === null || ssoResult === void 0 ? void 0 : ssoResult.auth) === null || _b === void 0 ? void 0 : _b.customer) &&
            this.auth.customer.registrationNumber === ssoResult.auth.customer.registrationNumber &&
            this.auth.customer.registrationNumberCountryCode === ssoResult.auth.customer.registrationNumberCountryCode;
        if (!isSameCustomer) {
            this.auth = ssoResult.auth;
        }
        if (this.redirectIfDealer && this.isDealer) {
            navigate(config.diilCrmUrl + languageSlug(this.translateStore.currentLanguage));
        }
        if (this.loggedIn && this.redirectUrl) {
            const url = this.redirectUrl;
            this.redirectUrl = null;
            navigate(url);
        }
        if (this.loggedIn) {
            this.closeSsoFrame();
        }
        if (this.loggedIn && this.loginCallback && typeof this.loginCallback === 'function') {
            this.loginCallback();
            this.loginCallback = null;
        }
        this.ssoClient.setOptions({ selectMandate: true });
        this.redirectIfDealer = false;
    }
    hasCustomerIdChanged(ssoResult) {
        return this.customerId !== (ssoResult.auth && ssoResult.auth.customer && ssoResult.auth.customer.customerId);
    }
    get isRepresentingWithDiilOrLegalMandate() {
        var _a, _b;
        return (_b = (_a = this.auth) === null || _a === void 0 ? void 0 : _a.mandates) === null || _b === void 0 ? void 0 : _b.some((authMandate) => (authMandate.diilAccounts && authMandate.diilAccounts.length) || authMandate.mandate === 'LEGAL');
    }
}
__decorate([
    observable
], SsoStore.prototype, "hardAuthRequired", void 0);
__decorate([
    observable
], SsoStore.prototype, "auth", void 0);
__decorate([
    observable
], SsoStore.prototype, "src", void 0);
__decorate([
    observable
], SsoStore.prototype, "clientState", void 0);
__decorate([
    observable
], SsoStore.prototype, "loggingOut", void 0);
__decorate([
    observable
], SsoStore.prototype, "initialSsoMessageReceived", void 0);
__decorate([
    observable
], SsoStore.prototype, "isSsoModalOpen", void 0);
__decorate([
    computed
], SsoStore.prototype, "loggedIn", null);
__decorate([
    computed
], SsoStore.prototype, "loggedInWithHardAuth", null);
__decorate([
    computed
], SsoStore.prototype, "loggedInWithWeakAuth", null);
__decorate([
    computed
], SsoStore.prototype, "loginLevelSufficient", null);
__decorate([
    computed
], SsoStore.prototype, "person", null);
__decorate([
    computed
], SsoStore.prototype, "customer", null);
__decorate([
    computed
], SsoStore.prototype, "isPrivateCustomer", null);
__decorate([
    computed
], SsoStore.prototype, "isRepresentingSelf", null);
__decorate([
    computed
], SsoStore.prototype, "dealer", null);
__decorate([
    computed
], SsoStore.prototype, "ssoFrameVisible", null);
__decorate([
    computed
], SsoStore.prototype, "ssoLoaded", null);
__decorate([
    action.bound
], SsoStore.prototype, "openSsoFrame", void 0);
__decorate([
    computed
], SsoStore.prototype, "customerId", null);
__decorate([
    computed
], SsoStore.prototype, "isDealer", null);
__decorate([
    action.bound
], SsoStore.prototype, "ensureLogin", void 0);
__decorate([
    action.bound
], SsoStore.prototype, "ensureHardLogin", void 0);
__decorate([
    action.bound
], SsoStore.prototype, "allowWeakAuth", null);
__decorate([
    action.bound
], SsoStore.prototype, "handleLogin", null);
__decorate([
    action.bound
], SsoStore.prototype, "setIframeUrl", void 0);
__decorate([
    action.bound
], SsoStore.prototype, "handleLogout", void 0);
__decorate([
    action.bound
], SsoStore.prototype, "doLogout", void 0);
__decorate([
    action.bound
], SsoStore.prototype, "setLoginParameters", void 0);
__decorate([
    action.bound
], SsoStore.prototype, "closeSsoFrame", void 0);
__decorate([
    computed
], SsoStore.prototype, "isRepresentingWithDiilOrLegalMandate", null);
export default SsoStore;
